import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '../../components/shared/PageHeader';
import { Input } from '../../components/shared/Input';
import { Select } from '../../components/shared/Select';
import { Skeleton } from '../../components/shared/Skeleton';
import { AnalyticsTabs } from '../../components/analytics/AnalyticsTabs';
import { RefereeRosterTable } from '../../components/analytics/RefereeRosterTable';
import { useRefereeRoster } from '../../hooks/use-analytics';
import { useAuth } from '../../contexts/auth-context';

const ALL_LICENCES = 'all';

// The Referees tab: every official in the federation, one row each, sortable.
//
// Search and licence filtering are client-side because the whole roster arrives in one payload — a
// federation is capped at 300 referees, so there is no page to fetch and a server round-trip per
// keystroke would buy nothing. Fetch failures render inline (never a toast) per our data-fetch
// convention.
export default function RefereeAnalyticsPage() {
  const { t } = useTranslation();
  const { role } = useAuth();
  const isSuperAdmin = role === 'super_admin';

  const { data, isLoading, isError, refetch } = useRefereeRoster();
  const [search, setSearch] = useState('');
  const [licence, setLicence] = useState(ALL_LICENCES);

  const referees = data?.referees;

  // Licence options come from the roster itself rather than a fixed list — licence_level is free
  // text on users, so a hard-coded set would silently hide anyone with an unexpected grade.
  const licences = useMemo(() => {
    const set = new Set<string>();
    for (const r of referees ?? []) if (r.licence_level) set.add(r.licence_level);
    return [...set].sort();
  }, [referees]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (referees ?? []).filter((r) => {
      if (licence !== ALL_LICENCES && (r.licence_level ?? '') !== licence) return false;
      if (!q) return true;
      return (r.full_name ?? '').toLowerCase().includes(q) || r.email.toLowerCase().includes(q);
    });
  }, [referees, search, licence]);

  return (
    <div>
      <PageHeader title={t('admin.analytics.dashboard.title')} sub={t('admin.analytics.dashboard.sub')} />
      <AnalyticsTabs />

      {isError && !data ? (
        <div className="rounded-lg border border-dashed border-danger bg-danger-tint px-6 py-12 text-center">
          <p className="text-sm text-danger">{t('admin.analytics.rosterPage.couldNotLoad')}</p>
          <button
            type="button"
            onClick={() => void refetch()}
            className="mt-2 text-xs font-medium text-ink underline underline-offset-2 hover:text-ink-2"
          >
            {t('common.tryAgain')}
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {isSuperAdmin && (
            <div className="rounded border border-line bg-surface-2 px-4 py-3 text-sm text-muted">
              {t('admin.analytics.dashboard.superAdminNote')}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <div className="w-full max-w-xs">
              <Input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('admin.analytics.rosterPage.searchPlaceholder')}
                aria-label={t('admin.analytics.rosterPage.searchAria')}
              />
            </div>
            {licences.length > 0 && (
              <div className="w-44">
                <Select value={licence} onChange={(e) => setLicence(e.target.value)} aria-label={t('admin.analytics.rosterPage.filterByLicenceAria')}>
                  <option value={ALL_LICENCES}>{t('admin.analytics.rosterPage.allLicences')}</option>
                  {licences.map((l) => (
                    <option key={l} value={l}>
                      {l}
                    </option>
                  ))}
                </Select>
              </div>
            )}
            {referees && (
              <span className="text-[12px] text-muted">
                {filtered.length === referees.length
                  ? t('admin.analytics.rosterPage.countAll', { count: referees.length })
                  : t('admin.analytics.rosterPage.countFiltered', { filtered: filtered.length, total: referees.length })}
              </span>
            )}
          </div>

          {isLoading || !data ? (
            <div className="space-y-2">
              {Array.from({ length: 8 }, (_, i) => (
                <Skeleton key={i} className="h-11 w-full" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="rounded border border-dashed border-line px-6 py-12 text-center text-sm text-muted">
              {referees && referees.length === 0
                ? t('admin.analytics.rosterPage.noRefereesYet')
                : t('admin.analytics.rosterPage.noMatchSearch')}
            </div>
          ) : (
            <>
              <RefereeRosterTable
                referees={filtered}
                windowDays={data.windowDays}
                minRankedAttempts={data.minRankedAttempts}
              />
              <p className="text-[11px] text-muted">
                {t('admin.analytics.rosterPage.rankFootnote', { min: data.minRankedAttempts, days: data.windowDays })}
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
}
